<template>
  <div>
    <!-- sticky control bar -->
    <NotificationControlBar
      v-model="item"
      :title="item && item.title"
      :role="role"
      :fromRoute="fromRoute"
      :deletedBy="user.username"
      :currState="currState"
      :isAdding="isAdding"
      :isBlog="isBlog"
      :isEdm="isEdm"
      :isDraft="item.status === 'DRAFT'"
      @edit-click="currState = STATES.EDITING"
      @cancel-click="onCancel"
      @preview-click="onPreview"
      @cancel-preview-click="currState = STATES.EDITING"
      @submit-click="onSubmit(false)"
      @resubmit-click="onSubmit(true)"
    >
    </NotificationControlBar>

    <!-- preview page: createdAt and updatedAt -->
    <div
      v-if="currState === STATES.VIEWING && role === 'admin'"
      style="width: 100%; max-width: 800px; margin: 0 auto"
    >
      <div style="margin: 1rem; display: flex; gap: 0.5rem">
        <div style="display: flex; flex-direction: column">
          <span>創建時間: </span>
          <span>最後更新時間: </span>
        </div>
        <div style="display: flex; flex-direction: column">
          <span>{{ item.createdAt }}</span>
          <span>{{ item.updatedAt }}</span>
        </div>
      </div>
    </div>

    <div class="setting-section-block">
      <div class="section-middle">
        <!-- preview page -->
        <div
          id="preview"
          v-if="
            (currState === STATES.VIEWING && item.status !== 'DRAFT') ||
              currState === STATES.PREVIEWING
          "
        >
          <!-- cover image (blog only) -->
          <template v-if="isBlog">
            <img
              v-if="item.image"
              :src="item.image | imagePath('notificationImages')"
              style="width: 100%"
            />
            <img
              v-else
              src="../../assets/img/notification/merchantcenter-blog.dummycover.png"
              style="width: 100%"
            />
          </template>

          <!-- tags and publishOn -->
          <div
            style="
              padding-bottom: 20px;
              display: flex;
              align-items: center;
              flex-wrap: wrap;
            "
          >
            <div class="tag" v-for="tag in item.tags" :key="tag">{{ tag }}</div>
            <div style="flex: 1"></div>
            <span class="publishOn">發佈日期：{{ formattedPublishOn }}</span>
          </div>

          <!-- title (edm only) -->
          <h1
            v-if="isEdm"
            style="
              padding: 0;
              line-height: 1.2;
              font-size: 2.5rem;
            "
          >
            {{ item.title }}
          </h1>

          <!-- content -->
          <div v-if="isBlog" v-html="item.content" style="margin: 1rem"></div>
          <div v-else-if="isEdm" style="overflow: auto">
            <iframe
              :src="item.htmlUrl | imagePath('notificationEdmHtml')"
              width="100%"
              frameborder="0"
              scrolling="no"
              onload="
                this.style.width=`${this.contentWindow.document.body.scrollWidth}px`
                this.style.height=`${this.contentWindow.document.body.scrollHeight}px`
              "
            ></iframe>
          </div>
        </div>

        <!-- edit form page -->
        <ValidationObserver v-else ref="form">
          <b-container>
            <div>
              <AGCInput
                v-model.trim="item.title"
                class="my-3"
                name="標題"
                label="標題"
                placeholder="輸入標題"
                rules="required|max:50"
                required
                :disabled="currState !== STATES.EDITING"
                autofocus
              ></AGCInput>

              <div class="mb-3">
                <DropZone
                  v-model="item.image"
                  name="封面圖片"
                  label="封面圖片"
                  sub-label="(最多1張，比例必須為16:9，檔案大小上限2mb)"
                  :rules="{ required: item.status !== 'DRAFT' }"
                  :required="item.status !== 'DRAFT'"
                  :readOnly="currState !== STATES.EDITING"
                  upload-type="notificationImages"
                  :max-files="1"
                  :acceptedAspectRatios="['16:9']"
                ></DropZone>
              </div>

              <ContentEditor
                v-if="isBlog"
                v-model="item.content"
                name="內容"
                label="內容"
                class="mb-4"
                :height="700"
                :rules="{ required: item.status !== 'DRAFT' }"
                :required="item.status !== 'DRAFT'"
                :readOnly="currState !== STATES.EDITING"
                menubar="edit view help"
                toolbar="
                  undo redo cut copy paste |
                  formatselect |
                  bold italic underline strikethrough superscript subscript forecolor backcolor |
                  alignleft aligncenter alignright alignjustify |
                  table bullist numlist outdent indent |
                  hr link anchor blockquote |
                  promotiondate youtube blogcontentimage ctabutton
                "
                :init="{ content_style: contentStyle }"
              ></ContentEditor>

              <div v-if="isEdm" class="mb-3">
                <DropZone
                  v-model="item.htmlUrl"
                  name="HTML"
                  label="HTML"
                  sub-label="(檔案大小上限2mb)"
                  :rules="{ required: item.status !== 'DRAFT' }"
                  :required="item.status !== 'DRAFT'"
                  :readOnly="currState !== STATES.EDITING"
                  upload-type="notificationEdmHtml"
                  acceptedFiles=".html"
                  :max-files="1"
                ></DropZone>
              </div>

              <TagsSearchBar
                v-model="item.tags"
                name="標籤"
                class="mb-4"
                rules="required"
                required
                :disabled="currState !== STATES.EDITING"
              />

              <AGCLabel :required="item.status !== 'DRAFT'">發佈日期</AGCLabel>
              <AGCDateTimePicker
                v-model="item.publishOn"
                name="發佈日期"
                class="datetime-picker mb-3"
                :rules="{ required: item.status !== 'DRAFT' }"
                :disabled="currState !== STATES.EDITING"
                has-time
              ></AGCDateTimePicker>

              <AGCLabel required>狀態</AGCLabel>
              <div class="select-group mb-4">
                <AGCSelectGroup
                  v-model="item.status"
                  name="狀態"
                  style="width: 3rem"
                  :disabled="currState !== STATES.EDITING"
                  :options="[
                    { value: 'PUBLISHED', text: '已發佈' },
                    { value: 'PENDING', text: '待發佈' },
                    { value: 'DRAFT', text: '草稿' }
                  ]"
                ></AGCSelectGroup>
              </div>

              <AGCCheckboxGroup
                v-model="item.displayLocation"
                name="發佈平台"
                label="發佈平台"
                subLabel="(可選多項)"
                :rules="{ required: item.status !== 'DRAFT' }"
                :required="item.status !== 'DRAFT'"
                :disabled="currState !== STATES.EDITING"
                :options="[
                  { item: 'MARKETING', name: 'Marketing Page' },
                  { item: 'NEWS', name: '最新消息' }
                ]"
              ></AGCCheckboxGroup>
            </div>
          </b-container>
        </ValidationObserver>
      </div>
    </div>

    <!-- overlays -->
    <ActionSuccessModal v-model="actionSuccessModalSettings"></ActionSuccessModal>
    <LoadingSpinner v-show="loading"></LoadingSpinner>
  </div>
</template>

<script>
import ActionSuccessModal from '@/components/ActionSuccessModal.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ContentEditor from '@/components/editor/ContentEditor.vue';
import AGCCheckboxGroup from '@/components/form/AGCCheckboxGroup.vue';
import AGCDateTimePicker from '@/components/form/AGCDateTimePicker.vue';
import AGCInput from '@/components/form/AGCInput.vue';
import AGCLabel from '@/components/form/AGCLabel.vue';
import AGCSelectGroup from '@/components/form/AGCSelectGroup.vue';
import DropZone from '@/components/form/DropZone.vue';
import NotificationControlBar from '@/components/notification/NotificationControlBar.vue';
import TagsSearchBar from '@/components/notification/TagsSearchBar.vue';
import imagePath from '@/filters/image-path';
import http from '@/helpers/http';
import formMixin from '@/mixins/form';
import { BContainer } from 'bootstrap-vue';
import cloneDeep from 'lodash.clonedeep';
import { mapGetters, mapState } from 'vuex';
import { getErrorMsg } from '../../helpers/error-msg';
import toastSettings from '../../helpers/toast-settings';

// copied from this Vue component's style
const contentStyle = `
h1 {
  margin-top: 0;
  margin-bottom: 0;
  padding: 20px 0;
  font-size: 3rem;
  font-weight: 200;
  color: #3a177a;
}
h2 {
  margin-top: 0;
  margin-bottom: 0.5rem;
  padding: 25px 0 20px 0;
  font-size: 2rem;
  font-weight: 500;
  color: #3a177a;
}
h3 {
  margin: 20px 0 10px 0;
  font-size: 1.5rem;
  font-weight: 500;
  color: #41334a;
}
p {
  margin: 0;
  padding: 10px 0;
  line-height: 160%;
  font-size: 1.3rem;
  font-weight: 300;
  color: #777;
}
a {
  font-weight: 500;
  color: #3a177a;
  text-decoration: underline;
}
`;

const STATES = Object.freeze({
  VIEWING: 0,
  EDITING: 1,
  PREVIEWING: 2,
});

export default {
  mixins: [formMixin],
  filters: {
    imagePath,
  },
  components: {
    ActionSuccessModal,
    LoadingSpinner,
    ContentEditor,
    AGCCheckboxGroup,
    AGCDateTimePicker,
    AGCInput,
    AGCLabel,
    AGCSelectGroup,
    DropZone,
    NotificationControlBar,
    TagsSearchBar,
    BContainer,
  },
  data: () => ({
    contentStyle,
    fromRoute: null,
    STATES,
    currState: STATES.VIEWING,
    item: {},
    loading: false,
    actionSuccessModalSettings: {
      show: false,
      msg: '已提交此消息',
      action: 'REMOVE',
      back: {
        location: '/admin/notification',
        text: '確定',
      },
    },
  }),
  computed: {
    ...mapGetters(['role']),
    ...mapState(['user']),
    id() {
      return this.$route.params.id;
    },
    apiUrl() {
      return (this.role === 'admin' || this.role === 'sale')
        ? `/agc-${this.role}-api/notification/details/`
        : '/agc-merchant-api/notification/details/';
    },
    isAdding() {
      return (
        this.$route.name === 'AddBlogNotification' || this.$route.name === 'AddEdmNotification'
      );
    },
    isBlog() {
      return (
        this.$route.name === 'AddBlogNotification'
        || this.item.type === 'BLOG'
        // default is blog for old notifications
        || (this.item.id && !this.item.type)
        || false
      );
    },
    isEdm() {
      return this.$route.name === 'AddEdmNotification' || this.item.type === 'EDM' || false;
    },
    formattedPublishOn() {
      const date = new Date(this.item.publishOn);
      const yyyy = date.getFullYear();
      const MM = (date.getMonth() + 1).toString().padStart(2, '0');
      const dd = date
        .getDate()
        .toString()
        .padStart(2, '0');
      return `${yyyy}/${MM}/${dd}`;
    },
  },
  methods: {
    onCancel() {
      this.currState = STATES.VIEWING;
      this.$refs.form.reset();
      this.item = cloneDeep({
        ...this.notificationStateBeforeUpdate,
      });
    },
    async onPreview() {
      if (!(await this.validate().toPromise())) {
        return;
      }
      this.currState = STATES.PREVIEWING;
    },
    async onSubmit(isResubmit) {
      // mapper before submitting data
      const data = {};

      data.notification = { ...this.item };
      if (this.isBlog) {
        data.notification.type = 'BLOG';
      } else if (this.isEdm) {
        data.notification.type = 'EDM';
      } else {
        // default is blog
        data.notification.type = 'BLOG';
      }
      data.notification.displayLocation = (this.item.displayLocation ?? []).join(',');
      delete data.notification.tags;

      data.tags = this.item.tags ?? [];

      this.loading = true;
      http(
        {
          method: 'POST',
          url: isResubmit
            ? `/agc-${this.role}-api/notification/resubmit`
            : `/agc-${this.role}-api/notification/submit`,
          data,
        },
        {
          error: ({ data: { msg } }) => {
            this.$bvToast.toast(getErrorMsg(msg), {
              ...toastSettings,
              title: '提交失敗',
              toaster: 'b-toaster-top-center',
            });
          },
          next: () => {
            this.actionSuccessModalSettings.show = true;
          },
          complete: () => {
            this.loading = false;
          },
        },
      ).toPromise();
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.fromRoute = from;
    });
  },
  async beforeMount() {
    this.loading = true;
    if (this.$route.name === 'EditNotification' && this.$route.params.id !== undefined) {
      this.item = await http({
        method: 'GET',
        url: this.apiUrl + this.$route.params.id,
      }).toPromise();
    } else {
      this.currState = STATES.EDITING;
    }

    // mapper after getting data
    if (this.item.displayLocation) {
      this.item.displayLocation = this.item.displayLocation.split(',');
    } else {
      this.item.displayLocation = [];
    }
    if (this.item.tags) {
      this.item.tags = this.item.tags.toString().split(',');
    } else {
      this.item.tags = [];
    }

    this.loading = false;
    this.notificationStateBeforeUpdate = cloneDeep(this.item);
  },
};
</script>

<style scoped lang="scss">
.setting-section-block {
  background: #fff;
  box-sizing: border-box;
  border: 1px solid #dde1e5;
  border-radius: 4px;
  margin: auto;
  overflow: hidden;
  max-width: 800px;
  margin-bottom: 10px;

  @include respond(phone) {
    margin: 10px 10px;
  }

  & .section-middle {
    width: 100%;
    display: inline-block;
    position: relative;
    min-height: 65px;
  }
}

::v-deep {
  #preview {
    max-width: 700px;
    margin: 1.25rem auto 0.5rem auto;

    // inferred from /merchantcenter/recruit/dist/scss/_shadow.scss
    .tag {
      margin: 6px 12px 6px 0;
      padding: 5px 10px;
      border: none;
      border-radius: 10px;
      font-size: 1.13rem;
      font-weight: 700;
      background: #dbdde0;
      color: #3a177a;
      cursor: auto;
      user-select: none;
    }

    // inferred from /merchantcenter/recruit/dist/scss/agc_style.scss
    .publishOn {
      font-weight: 400;
      font-size: 1.1rem;
      color: #777;
    }

    h1 {
      margin-top: 0;
      margin-bottom: 0;
      padding: 20px 0;
      font-size: 3rem;
      font-weight: 200;
      color: #3a177a;
    }
    h2 {
      margin-top: 0;
      margin-bottom: 0.5rem;
      padding: 25px 0 20px 0;
      font-size: 2rem;
      font-weight: 500;
      color: #3a177a;
    }
    h3 {
      margin: 20px 0 10px 0;
      font-size: 1.5rem;
      font-weight: 500;
      color: #41334a;
    }
    p {
      margin: 0;
      padding: 10px 0;
      line-height: 160%;
      font-size: 1.3rem;
      font-weight: 300;
      color: #777;
    }
    a {
      font-weight: 500;
      color: #3a177a;
      text-decoration: underline;
    }
  }

  .datetime-picker {
    @include respond(phone) {
      div.d-flex > div:first-of-type,
      div:nth-of-type(2) {
        width: 50%;
        input.mx-input {
          width: 100%;
        }
      }
    }
  }

  .select-group {
    width: max-content;
    @include respond(phone) {
      width: 100%;
    }
  }
}
</style>
